import type { Canvas, CanvasNode, CanvasEdge, CanvasAnnotation } from '@/features/Canvas/types/canvas';
import type { ResultSet } from '@/features/ResultList/types/results.d';
import type { SortField, SortDirection } from '@/features/Projects/types/projects';

export type CanvasSortMode = 'date' | 'name';
export type ObjectSortMode = 'relationships' | 'alphabetical' | 'type';
export type AnnotationSortMode = 'alphabetical' | 'date';

export const getAnnotationDisplayName = (annotation: CanvasAnnotation): string => {
  const trimmed = annotation.text.trim();
  return trimmed || 'Empty annotation';
};

export const getCanvasNodeDisplayName = (node: CanvasNode): string =>
  node.names[0] || node.id;

export const getCanvasNodePrimaryCategory = (
  node: CanvasNode | null | undefined,
): string | undefined => node?.types[0];

export type CanvasSearchMatch = {
  label: string;
  value: string;
};

const formatSearchMatch = ({ label, value }: CanvasSearchMatch): string =>
  `${label} (${value})`;

export const formatCanvasSearchMatchTooltip = (matches: CanvasSearchMatch[]): string =>
  `Matched on: ${matches.map(formatSearchMatch).join(', ')}`;

const collectCanvasNodeSearchMatches = (
  node: CanvasNode,
  search: string,
): CanvasSearchMatch[] => {
  if (!search) return [];

  const lower = search.toLowerCase();
  const matches: CanvasSearchMatch[] = [];
  const seen = new Set<string>();

  const addMatch = (label: string, value: string) => {
    const key = `${label}:${value}`;
    if (seen.has(key)) return;
    seen.add(key);
    matches.push({ label, value });
  };

  node.names.forEach(name => {
    if (name.toLowerCase().includes(lower)) addMatch('name', name);
  });
  node.curies.forEach(curie => {
    if (curie.toLowerCase().includes(lower)) addMatch('curie', curie);
  });

  return matches;
};

export const canvasNodeMatchesSearch = (node: CanvasNode, search: string): boolean =>
  collectCanvasNodeSearchMatches(node, search).length > 0;

export const getCanvasNodeSearchMatchesOutsideDisplayName = (
  node: CanvasNode,
  search: string,
): CanvasSearchMatch[] => {
  if (!search) return [];

  const displayName = getCanvasNodeDisplayName(node);
  if (displayName.toLowerCase().includes(search.toLowerCase())) return [];

  return collectCanvasNodeSearchMatches(node, search);
};

const collectCanvasAnnotationSearchMatches = (
  annotation: CanvasAnnotation,
  search: string,
): CanvasSearchMatch[] => {
  if (!search) return [];

  const lower = search.toLowerCase();
  if (!annotation.text.toLowerCase().includes(lower)) return [];

  return [{ label: 'text', value: annotation.text.trim() || annotation.text }];
};

export const canvasAnnotationMatchesSearch = (
  annotation: CanvasAnnotation,
  search: string,
): boolean => collectCanvasAnnotationSearchMatches(annotation, search).length > 0;

export const getCanvasAnnotationSearchMatchesOutsideDisplayName = (
  annotation: CanvasAnnotation,
  search: string,
): CanvasSearchMatch[] => {
  if (!search) return [];

  const displayName = getAnnotationDisplayName(annotation);
  if (displayName.toLowerCase().includes(search.toLowerCase())) return [];

  return collectCanvasAnnotationSearchMatches(annotation, search);
};

export const filterCanvasAnnotations = (
  annotations: CanvasAnnotation[],
  search: string,
): CanvasAnnotation[] => {
  if (!search) return annotations;
  return annotations.filter(annotation => canvasAnnotationMatchesSearch(annotation, search));
};

export const sortCanvasAnnotations = (
  annotations: CanvasAnnotation[],
  mode: AnnotationSortMode,
): CanvasAnnotation[] =>
  [...annotations].sort((a, b) => {
    switch (mode) {
      case 'alphabetical':
        return getAnnotationDisplayName(a).localeCompare(getAnnotationDisplayName(b));
      case 'date':
        return new Date(b.timeCreated).getTime() - new Date(a.timeCreated).getTime();
      default:
        return 0;
    }
  });

export const getCanvasNodeCount = (canvas: Canvas): number => Object.keys(canvas.nodes).length;

export const getCanvasEdgeCount = (canvas: Canvas): number => Object.keys(canvas.edges).length;

/** True when the canvas has nodes or annotations that can appear on the graph. */
export const canvasHasExportableGraph = (canvas: {
  nodes: Record<string, unknown>;
  annotations: readonly unknown[];
}): boolean => Object.keys(canvas.nodes).length > 0 || canvas.annotations.length > 0;

type CountLabels = { singular: string; plural: string };

const getCanvasCountDisplay = (canvas: Canvas, count: number, labels?: CountLabels): string => {
  if (!canvas.graphLoaded) return '-';
  if (!labels) return String(count);
  return `${count} ${count === 1 ? labels.singular : labels.plural}`;
};

export const getCanvasObjectCountDisplay = (canvas: Canvas, labels?: CountLabels): string =>
  getCanvasCountDisplay(canvas, getCanvasNodeCount(canvas), labels);

export const getCanvasRelationshipCountDisplay = (canvas: Canvas, labels?: CountLabels): string =>
  getCanvasCountDisplay(canvas, getCanvasEdgeCount(canvas), labels);

const countEdgesPerNode = (edges: Record<string, CanvasEdge>): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const edge of Object.values(edges)) {
    counts.set(edge.subject, (counts.get(edge.subject) ?? 0) + 1);
    // A self-loop is a single incident edge, not two.
    if (edge.object === edge.subject) continue;
    counts.set(edge.object, (counts.get(edge.object) ?? 0) + 1);
  }
  return counts;
};

export const sortCanvasNodes = (
  nodes: CanvasNode[],
  mode: ObjectSortMode,
  edges: Record<string, CanvasEdge>,
): CanvasNode[] => {
  if (mode === 'relationships') {
    const counts = countEdgesPerNode(edges);
    return [...nodes].sort((a, b) => (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0));
  }

  return [...nodes].sort((a, b) => {
    switch (mode) {
      case 'alphabetical':
        return getCanvasNodeDisplayName(a).localeCompare(getCanvasNodeDisplayName(b));
      case 'type':
        return (a.types[0] ?? '').localeCompare(b.types[0] ?? '');
      default:
        return 0;
    }
  });
};

export const filterCanvasNodes = (nodes: CanvasNode[], search: string): CanvasNode[] => {
  if (!search) return nodes;
  return nodes.filter(node => canvasNodeMatchesSearch(node, search));
};

export const mergeCanvasNode = (existing: CanvasNode, incoming: CanvasNode): CanvasNode => ({
  ...existing,
  names: Array.from(new Set([...existing.names, ...incoming.names])),
  types: Array.from(new Set([...existing.types, ...incoming.types])),
  curies: Array.from(new Set([...existing.curies, ...incoming.curies])),
});

export const filterCanvasesBySearch = (canvases: Canvas[], searchTerm: string): Canvas[] => {
  if (!searchTerm) return canvases;
  const lower = searchTerm.toLowerCase();
  return canvases.filter(c => c.label.toLowerCase().includes(lower));
};

const compareCanvases = (a: Canvas, b: Canvas, sortField: SortField): number => {
  switch (sortField) {
    case 'name': return a.label.localeCompare(b.label);
    case 'objects': return getCanvasNodeCount(a) - getCanvasNodeCount(b);
    case 'relationships': return getCanvasEdgeCount(a) - getCanvasEdgeCount(b);
    case 'created': return new Date(a.timeCreated).getTime() - new Date(b.timeCreated).getTime();
    case 'lastSeen': return new Date(a.timeUpdated).getTime() - new Date(b.timeUpdated).getTime();
    default: return 0;
  }
};

export const sortCanvases = (
  canvases: Canvas[],
  sortField: SortField = 'created',
  sortDirection: SortDirection = 'desc',
): Canvas[] => {
  const direction = sortDirection === 'asc' ? 1 : -1;
  return [...canvases].sort((a, b) => direction * compareCanvases(a, b, sortField));
};

const LARGE_RESULT_THRESHOLD = 50;

export const resultCountExceedsThreshold = (resultSet: ResultSet): boolean =>
  resultSet.data.results.length > LARGE_RESULT_THRESHOLD;
